import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { SectionLabel, InsightsCard, useRevealAnimations } from "../components/shared";
import { services, sectors, practices } from "../data/content";
import SEO from "../components/SEO";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HomePage() {
  const [insightsPreview, setInsightsPreview] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/articles?limit=3`).then(r => {
      setInsightsPreview(r.data.articles || []);
    }).catch(() => {});
  }, []);

  useRevealAnimations(insightsPreview.length);

  return (
    <main data-testid="site-main-content">
      <SEO 
        description="Axentia Global is a specialist executive search firm for energy, semiconductor, and industrial businesses across Asia. Sector-specialist. Asia-focused."
        path="/"
      />
      <section id="hero" className="hero-section" style={{ backgroundImage: "url('/images/hero-fallback.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }} data-testid="hero-section">
        <video
          className="hero-video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-background-video"
        >
          <source src="/videos/hero-video.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay" data-testid="hero-overlay" />
        <div className="shell hero-shell">
          <div className="hero-content" data-animate="fade-up" data-testid="hero-content">
            <h1 className="hero-title" data-testid="hero-headline">
              The Right Leader Changes <em>Everything.</em>
            </h1>
            <p className="hero-text" data-testid="hero-subtext">
              Helping semiconductor, industrial, and energy companies build
              leadership teams across Asia.
            </p>
            <div className="hero-actions" data-testid="hero-action-buttons-group">
              <Button asChild className="ax-btn ax-btn-solid" data-testid="hero-start-conversation-button">
                <Link to="/contact">Discuss your hiring needs</Link>
              </Button>
              <Button asChild className="ax-btn ax-btn-ghost" data-testid="hero-who-we-are-button">
                <Link to="/about">Who We Are</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="section" data-testid="services-section">
        <div className="shell">
          <header className="center-head" data-animate="fade-up" data-testid="services-header-row">
            <h2 className="section-heading" data-testid="services-heading">
              Three Ways We Work With Clients
            </h2>
          </header>
          <div className="services-grid" data-testid="services-cards-grid">
            {services.map((service, index) => (
              <Link
                to={`/services/${service.slug}`}
                key={service.slug}
                className="service-card-link"
                data-testid={`service-card-link-${index + 1}`}
              >
                <article
                  className="service-card"
                  data-animate="fade-up"
                  style={{ transitionDelay: `${index * 120}ms` }}
                  data-testid={`service-card-${index + 1}`}
                >
                  <div className="service-image-wrap">
                    <img src={service.image} alt={service.title} />
                  </div>
                  <p className="service-number">{service.number}</p>
                  <h3 className="card-title">{service.title}</h3>
                  <p className="card-body">{service.brief}</p>
                </article>
              </Link>
            ))}
          </div>
          <div className="insights-view-all" data-animate="fade-up" data-testid="services-explore-all">
            <Button asChild className="ax-btn ax-btn-ghost" data-testid="services-explore-all-button">
              <Link to="/services">Explore our services &rarr;</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-alt" data-testid="practices-section">
        <div className="shell">
          <header className="center-head" data-animate="fade-up" data-testid="practices-header">
            <h2 className="section-heading" data-testid="practices-heading">
              Four Practice Areas. One Standard.
            </h2>
            <p className="section-body centered" data-testid="practices-subtext">
              Our practice structure reflects the way our clients operate — organised
              around the functions and markets where leadership decisions matter most.
            </p>
          </header>
          <div className="practices-grid" data-testid="practices-cards-grid">
            {practices.map((practice, index) => {
              const anchor = practice.slug.replace('-leadership', '');
              return (
              <Link
                to={`/expertise#${anchor}`}
                key={practice.slug}
                className="practice-card-link"
                data-testid={`practice-card-link-${index + 1}`}
              >
                <article
                  className="practice-card"
                  data-animate="fade-up"
                  style={{ transitionDelay: `${index * 100}ms` }}
                  data-testid={`practice-card-${index + 1}`}
                >
                  <h3 data-testid={`practice-card-title-${index + 1}`}>{practice.name}</h3>
                  <p className="practice-card-functions" data-testid={`practice-card-functions-${index + 1}`}>
                    {practice.details}
                  </p>
                  <span className="practice-card-cta">
                    Learn more &rarr;
                  </span>
                </article>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section" data-testid="sectors-section">
        <div className="shell">
          <header className="center-head" data-animate="fade-up" data-testid="sectors-header">
            <h2 className="section-heading" data-testid="sectors-heading">
              We Work Exclusively Across Energy, Semiconductor, Industrial & Life Sciences Markets
            </h2>
            <p className="section-body centered" data-testid="sectors-subtext">
              These are the only markets we serve. From
              semiconductor fabs to LNG terminals, from refineries to renewable energy
              platforms — our focus is the reason our candidates and clients trust us with
              their most sensitive conversations.
            </p>
          </header>
          <div className="sector-grid" data-testid="sectors-cards-grid">
            {sectors.map((sector, index) => (
              <Link to={`/sectors#${sector.slug}`} key={sector.slug} className="sector-card-link" data-testid={`sector-card-link-${index + 1}`}>
                <article
                  className="sector-card"
                  data-animate="fade-up"
                  style={{ transitionDelay: `${index * 90}ms` }}
                  data-testid={`sector-card-${index + 1}`}
                >
                  <img src={sector.image} alt={sector.title} />
                  <div className="sector-overlay" />
                  <div className="sector-copy">
                    <h3>{sector.title}</h3>
                  </div>
                </article>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section insights-section" data-testid="insights-section">
        <div className="shell">
          <header className="center-head" data-animate="fade-up" data-testid="insights-header">
            <h2 className="section-heading" data-testid="insights-heading">
              Latest Perspectives
            </h2>
          </header>
          <div className="insights-preview-grid" data-testid="insights-preview-grid">
            {insightsPreview.map((article, index) => (
              <InsightsCard key={article.slug} article={article} index={index} baseTestId="insights-preview" />
            ))}
          </div>
          <div className="insights-view-all" data-animate="fade-up" data-testid="insights-view-all-wrap">
            <Button asChild className="ax-btn ax-btn-ghost" data-testid="insights-view-all-button">
              <Link to="/insights">View all insights &rarr;</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-alt" data-testid="home-executives-band">
        <div className="shell center-head" data-animate="fade-up">
          <h2 className="section-heading" data-testid="home-executives-heading">For Executives</h2>
          <p className="section-body centered" data-testid="home-executives-body">
            If you are a senior executive considering a first or next board role, we offer a
            private assessment of where a board would realistically appoint you, and why.
          </p>
          <Button asChild className="ax-btn ax-btn-ghost" data-testid="home-executives-button">
            <Link to="/executives/board-candidacy-advisory">Board Candidacy Advisory</Link>
          </Button>
        </div>
      </section>

      <section
        className="contact-cta"
        style={{
          backgroundImage:
            "url('/images/office-band.webp')",
        }}
        data-testid="contact-cta-section"
      >
        <div className="contact-overlay" />
        <div className="shell contact-content" data-animate="fade-up" data-testid="contact-content">
          <h2 className="section-heading" data-testid="contact-heading">
            Every Engagement Begins With a Conversation
          </h2>
          <p className="section-body centered" data-testid="contact-subtext">
            Whether you are filling a specific role, thinking about succession, or
            simply want to understand the leadership landscape in your sector — we are
            happy to talk.
          </p>
          <div className="hero-actions" data-testid="contact-action-buttons-group">
            <Button asChild className="ax-btn ax-btn-solid" data-testid="contact-get-in-touch-button">
              <Link to="/contact">Get in Touch</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
